/**
 * @module dom/query
 *
 * Chainable collection of elements. The idea is the same as jQuery: select,
 * traverse, and manipulate with few lines. The difference is in strict typing,
 * native iteration with `for...of`, zero dependencies, and integration with
 * Voodoo's runtime: removing or emptying elements unmounts the reactive effects
 * tied to them, preventing memory leaks.
 *
 * ```js
 * V.query('.card')
 *   .addClass('active')
 *   .on('click', '.button', function () { V.query(this).closest('.card').remove() })
 * ```
 */

import { handleError } from '../reactivity';
import { whenBodyReady } from '../runtime/boot';
import { destroy as destroyNode, findScope, walk as walkNode } from '../runtime/walker';
import {
  fadeIn as fadeInElement,
  fadeOut as fadeOutElement,
  slideDown as slideDownElement,
  slideUp as slideUpElement,
} from './transition';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Function executed when the document becomes ready. */
export type ReadyCallback = () => void;

/** Event handler. `this` points to the element that matched the filter. */
export type QueryEventHandler = (this: HTMLElement, event: Event) => unknown;

/** Everything that `query()` accepts as input. */
export type QueryInput =
  | string
  | Node
  | Element
  | Document
  | DocumentFragment
  | ArrayLike<Node>
  | VoodooCollection
  | ReadyCallback
  | null
  | undefined;

/** Filter accepted by `filter`, `not`, and `is`. */
export type QueryFilter = string | ((el: HTMLElement, index: number) => boolean);

/** Coordinates returned by `offset` and `position`. */
export interface QueryPoint {
  top: number;
  left: number;
}

/** Value accepted when writing simple attributes and properties. */
export type QueryValue = string | number | boolean | null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** CSS properties that do not receive `px` automatically. */
const UNITLESS = new Set([
  'animation-iteration-count',
  'aspect-ratio',
  'border-image-slice',
  'column-count',
  'flex',
  'flex-grow',
  'flex-shrink',
  'font-weight',
  'grid-area',
  'grid-column',
  'grid-row',
  'line-height',
  'opacity',
  'order',
  'orphans',
  'scale',
  'tab-size',
  'widows',
  'z-index',
  'zoom',
]);

/** Converts `backgroundColor` to `background-color`. */
function kebab(property: string): string {
  if (property.startsWith('--')) return property;
  return property.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);
}

/** Removes duplicates while preserving input order. */
function distinct(list: HTMLElement[]): HTMLElement[] {
  if (list.length < 2) return list;
  return Array.from(new Set(list));
}

/** Splits a list of space-separated names. */
function names(value: string): string[] {
  return String(value ?? '')
    .split(/\s+/)
    .filter(Boolean);
}

/** Converts an HTML string to its corresponding elements. */
function parseHtml(html: string): HTMLElement[] {
  if (typeof document === 'undefined') return [];
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const out: HTMLElement[] = [];
  for (const child of Array.from(template.content.children)) out.push(child as HTMLElement);
  return out;
}

/** Detects a string that describes HTML instead of a selector. */
function looksLikeHtml(text: string): boolean {
  return text.length > 2 && text.charCodeAt(0) === 60 /* < */ && text.endsWith('>');
}

/** Roots where the selector will be applied. */
function contextRoots(context?: QueryInput): ParentNode[] {
  if (context == null) return typeof document === 'undefined' ? [] : [document];
  if (context instanceof VoodooCollection) return context.toArray();
  if (typeof context === 'string') return resolve(context);
  if (typeof context === 'function') return typeof document === 'undefined' ? [] : [document];
  const list = resolve(context);
  if (list.length) return list;
  return typeof document === 'undefined' ? [] : [document];
}

/** Normalizes any accepted input into a list of elements. */
function resolve(input: QueryInput, context?: QueryInput): HTMLElement[] {
  if (input == null) return [];

  if (typeof input === 'string') {
    const text = input.trim();
    if (!text) return [];
    if (looksLikeHtml(text)) return parseHtml(text);
    const out: HTMLElement[] = [];
    for (const root of contextRoots(context)) {
      try {
        for (const found of Array.from(root.querySelectorAll(text))) out.push(found as HTMLElement);
      } catch {
        // Invalid selector returns empty collection instead of crashing the page.
      }
    }
    return distinct(out);
  }

  if (input instanceof VoodooCollection) return input.toArray();

  if (typeof input === 'function') return [];

  const node = input as Node;
  if (typeof node.nodeType === 'number') {
    if (node.nodeType === 1) return [node as HTMLElement];
    if (node.nodeType === 9) {
      const doc = node as Document;
      return doc.documentElement ? [doc.documentElement] : [];
    }
    if (node.nodeType === 11) {
      return Array.from((node as DocumentFragment).children) as HTMLElement[];
    }
    return [];
  }

  const arrayLike = input as ArrayLike<Node>;
  if (typeof arrayLike.length === 'number') {
    const out: HTMLElement[] = [];
    for (let i = 0; i < arrayLike.length; i++) {
      const item = arrayLike[i];
      if (item && item.nodeType === 1) out.push(item as HTMLElement);
    }
    return distinct(out);
  }

  return [];
}

/** Converts content accepted by `append` and friends to nodes ready for insertion. */
function contentNodes(content: QueryInput): Node[] {
  if (content == null) return [];
  if (typeof content === 'string') {
    const text = content;
    if (looksLikeHtml(text.trim())) return parseHtml(text);
    return [document.createTextNode(text)];
  }
  if (content instanceof VoodooCollection) return content.toArray();
  if (typeof content === 'function') return [];
  const node = content as Node;
  if (typeof node.nodeType === 'number') return [node];
  const arrayLike = content as ArrayLike<Node>;
  if (typeof arrayLike.length === 'number') {
    const out: Node[] = [];
    for (let i = 0; i < arrayLike.length; i++) if (arrayLike[i]) out.push(arrayLike[i]);
    return out;
  }
  return [];
}

/** Applies a style property while handling default units. */
function setStyle(el: HTMLElement, property: string, value: string | number | null): void {
  const name = kebab(property);
  if (value === null || value === '') {
    el.style.removeProperty(name);
    return;
  }
  const text =
    typeof value === 'number' && !UNITLESS.has(name) && !name.startsWith('--')
      ? `${value}px`
      : String(value);
  el.style.setProperty(name, text);
}

/** Applies multiple properties at once. */
function applyStyles(el: HTMLElement, values: Record<string, string | number | null>): void {
  for (const [property, value] of Object.entries(values)) setStyle(el, property, value);
}

/** Reads a `dataset` value, converting JSON, numbers, and booleans when possible. */
function parseDataValue(raw: string | undefined): unknown {
  if (raw === undefined) return undefined;
  if (raw === '') return '';
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null') return null;
  if (/^-?\d+(\.\d+)?$/.test(raw)) return Number(raw);
  const first = raw.charCodeAt(0);
  if (first === 123 /* { */ || first === 91 /* [ */ || first === 34 /* " */) {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

/** Converts `my-key` to `myKey`, the format used by `dataset`. */
function datasetKey(key: string): string {
  return key.replace(/-([a-z0-9])/g, (_all, ch: string) => ch.toUpperCase());
}

/** Original display value saved by `hide`, restored by `show`. */
const savedDisplay = new WeakMap<HTMLElement, string>();

/** Checks if an element is currently hidden. */
function elementHidden(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return true;
  if (el.style.display === 'none') return true;
  return !el.isConnected ? false : getComputedStyle(el).display === 'none';
}

/** Shows an element by restoring its previous `display` value. */
function showElement(el: HTMLElement): void {
  el.removeAttribute('hidden');
  const previous = savedDisplay.get(el);
  if (previous !== undefined && previous !== 'none') el.style.display = previous;
  else el.style.removeProperty('display');
  if (el.isConnected && getComputedStyle(el).display === 'none') el.style.display = 'block';
}

/** Hides an element while saving its current `display` value. */
function hideElement(el: HTMLElement): void {
  const current = el.style.display;
  if (current && current !== 'none') savedDisplay.set(el, current);
  el.style.display = 'none';
}

/** Form controls considered by `serialize`. */
const FORM_CONTROLS = 'input,select,textarea';

/** Reads name-value pairs from a form or a section with fields. */
function formControls(el: HTMLElement): HTMLElement[] {
  if (el.matches(FORM_CONTROLS)) return [el];
  return Array.from(el.querySelectorAll<HTMLElement>(FORM_CONTROLS));
}

/** Indicates whether a control is included in default form serialization. */
function isSerializable(control: HTMLElement): boolean {
  const field = control as HTMLInputElement;
  if (!field.name || field.disabled) return false;
  const type = (field.getAttribute('type') || '').toLowerCase();
  if (type === 'file' || type === 'submit' || type === 'reset' || type === 'button') return false;
  if ((type === 'checkbox' || type === 'radio') && !field.checked) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Event registry, necessary for `off` to work with delegation
// ---------------------------------------------------------------------------

interface BoundEvent {
  type: string;
  selector: string | null;
  handler: QueryEventHandler;
  wrapped: EventListener;
  options: AddEventListenerOptions;
}

const eventStore = new WeakMap<HTMLElement, BoundEvent[]>();

function bindingsOf(el: HTMLElement): BoundEvent[] {
  let list = eventStore.get(el);
  if (!list) eventStore.set(el, (list = []));
  return list;
}

// ---------------------------------------------------------------------------
// The collection
// ---------------------------------------------------------------------------

/**
 * Immutable list of elements with chainable methods. Instances are created
 * by `query()`, never with `new` in user code.
 */
export class VoodooCollection implements Iterable<HTMLElement> {
  /** Indexed access, as in `collection[0]`. */
  [index: number]: HTMLElement;

  /** Number of elements in the collection. */
  readonly length: number;

  /** Elements of the collection, in the order they were found. */
  readonly elements: HTMLElement[];

  constructor(elements: HTMLElement[] = []) {
    this.elements = elements;
    this.length = elements.length;
    const indexed = this as unknown as Record<number, HTMLElement>;
    for (let i = 0; i < elements.length; i++) indexed[i] = elements[i];
  }

  /** Enables `for (const el of query('.item'))`. */
  [Symbol.iterator](): Iterator<HTMLElement> {
    return this.elements[Symbol.iterator]();
  }

  // -------------------------------------------------------------------------
  // Traversal
  // -------------------------------------------------------------------------

  /** Descendants that match the selector. */
  find(selector: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      try {
        for (const found of Array.from(el.querySelectorAll(selector))) out.push(found as HTMLElement);
      } catch {
        // Invalid selector does not interrupt the chain.
      }
    }
    return new VoodooCollection(distinct(out));
  }

  /** Nearest ancestor, including the element itself. */
  closest(selector: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      const found = el.closest(selector);
      if (found) out.push(found as HTMLElement);
    }
    return new VoodooCollection(distinct(out));
  }

  /** Parent element of each item, optionally filtered. */
  parent(selector?: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      const parent = el.parentElement;
      if (parent && (!selector || parent.matches(selector))) out.push(parent);
    }
    return new VoodooCollection(distinct(out));
  }

  /** All ancestors, from nearest to farthest. */
  parents(selector?: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      let current = el.parentElement;
      while (current) {
        if (!selector || current.matches(selector)) out.push(current);
        current = current.parentElement;
      }
    }
    return new VoodooCollection(distinct(out));
  }

  /** Direct children, optionally filtered. */
  children(selector?: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      for (const child of Array.from(el.children)) {
        if (!selector || child.matches(selector)) out.push(child as HTMLElement);
      }
    }
    return new VoodooCollection(distinct(out));
  }

  /** Siblings, excluding the elements themselves. */
  siblings(selector?: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      const parent = el.parentElement;
      if (!parent) continue;
      for (const child of Array.from(parent.children)) {
        if (child === el) continue;
        if (!selector || child.matches(selector)) out.push(child as HTMLElement);
      }
    }
    return new VoodooCollection(distinct(out));
  }

  /** Next sibling of each element. */
  next(selector?: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      const sibling = el.nextElementSibling as HTMLElement | null;
      if (sibling && (!selector || sibling.matches(selector))) out.push(sibling);
    }
    return new VoodooCollection(distinct(out));
  }

  /** Previous sibling of each element. */
  prev(selector?: string): VoodooCollection {
    const out: HTMLElement[] = [];
    for (const el of this.elements) {
      const sibling = el.previousElementSibling as HTMLElement | null;
      if (sibling && (!selector || sibling.matches(selector))) out.push(sibling);
    }
    return new VoodooCollection(distinct(out));
  }

  /** Only the first element. */
  first(): VoodooCollection {
    return this.eq(0);
  }

  /** Only the last element. */
  last(): VoodooCollection {
    return this.eq(-1);
  }

  /** Element at the specified position. Negative indices count from the end. */
  eq(index: number): VoodooCollection {
    const position = index < 0 ? this.elements.length + index : index;
    const el = this.elements[position];
    return new VoodooCollection(el ? [el] : []);
  }

  /** Keeps only elements that pass the filter. */
  filter(test: QueryFilter): VoodooCollection {
    const out = this.elements.filter((el, index) =>
      typeof test === 'function' ? test(el, index) : el.matches(test)
    );
    return new VoodooCollection(out);
  }

  /** Removes from the collection elements that pass the filter. */
  not(test: QueryFilter): VoodooCollection {
    const out = this.elements.filter((el, index) =>
      typeof test === 'function' ? !test(el, index) : !el.matches(test)
    );
    return new VoodooCollection(out);
  }

  /** Keeps elements that contain the specified descendant. */
  has(target: string | Element): VoodooCollection {
    const out = this.elements.filter((el) =>
      typeof target === 'string' ? el.querySelector(target) !== null : el.contains(target)
    );
    return new VoodooCollection(out);
  }

  /** Checks if at least one element matches the filter. */
  is(test: QueryFilter): boolean {
    return this.elements.some((el, index) =>
      typeof test === 'function' ? test(el, index) : el.matches(test)
    );
  }

  /** Projects each element to a value and returns a regular array. */
  map<T>(fn: (el: HTMLElement, index: number) => T): T[] {
    return this.elements.map((el, index) => fn(el, index));
  }

  /** Iterates over the collection. Inside the function, `this` is the current element. */
  each(fn: (this: HTMLElement, el: HTMLElement, index: number) => unknown): this {
    for (let i = 0; i < this.elements.length; i++) {
      const el = this.elements[i];
      if (fn.call(el, el, i) === false) break;
    }
    return this;
  }

  /** Without arguments returns the array; with index returns an element. */
  get(): HTMLElement[];
  get(index: number): HTMLElement | undefined;
  get(...rest: unknown[]): HTMLElement[] | HTMLElement | undefined {
    if (!rest.length) return this.toArray();
    const index = Number(rest[0]);
    return this.elements[index < 0 ? this.elements.length + index : index];
  }

  /** Copy of elements as a regular array. */
  toArray(): HTMLElement[] {
    return this.elements.slice();
  }

  /** Joins other elements to the collection without duplication. */
  add(input: QueryInput, context?: QueryInput): VoodooCollection {
    return new VoodooCollection(distinct([...this.elements, ...resolve(input, context)]));
  }

  /** Slice of the collection with the same semantics as `Array.prototype.slice`. */
  slice(start?: number, end?: number): VoodooCollection {
    return new VoodooCollection(this.elements.slice(start, end));
  }

  // -------------------------------------------------------------------------
  // Content
  // -------------------------------------------------------------------------

  /** Reads the text of the first element or writes to all. */
  text(): string;
  text(value: string | number | null): this;
  text(...rest: unknown[]): string | this {
    if (!rest.length) return this.elements[0]?.textContent ?? '';
    const value = rest[0];
    const text = value == null ? '' : String(value);
    for (const el of this.elements) {
      for (const child of Array.from(el.childNodes)) destroyNode(child);
      el.textContent = text;
    }
    return this;
  }

  /** Reads the inner HTML of the first element or writes to all. */
  html(): string;
  html(value: string | null): this;
  html(...rest: unknown[]): string | this {
    if (!rest.length) return this.elements[0]?.innerHTML ?? '';
    const value = rest[0];
    const text = value == null ? '' : String(value);
    for (const el of this.elements) {
      // Unmount old content to avoid orphaned effects.
      for (const child of Array.from(el.childNodes)) destroyNode(child);
      el.innerHTML = text;
    }
    return this;
  }

  /** Reads the value of the first field or writes to all. */
  val(): string | string[];
  val(value: string | number | boolean | string[] | null): this;
  val(...rest: unknown[]): string | string[] | this {
    if (!rest.length) {
      const field = this.elements[0] as HTMLInputElement | undefined;
      if (!field) return '';
      const select = field as unknown as HTMLSelectElement;
      if (field.tagName === 'SELECT' && select.multiple) {
        return Array.from(select.selectedOptions).map((option) => option.value);
      }
      if (field.type === 'checkbox') return field.checked ? field.value || 'on' : '';
      return field.value ?? '';
    }

    const value = rest[0];
    for (const el of this.elements) {
      const field = el as HTMLInputElement;
      const select = el as unknown as HTMLSelectElement;
      if (field.tagName === 'SELECT' && select.multiple) {
        const wanted = (Array.isArray(value) ? value : [value]).map(String);
        for (const option of Array.from(select.options)) option.selected = wanted.includes(option.value);
        continue;
      }
      if (field.type === 'checkbox' || field.type === 'radio') {
        field.checked = Array.isArray(value)
          ? value.map(String).includes(field.value)
          : value === true || String(value) === field.value;
        continue;
      }
      field.value = value == null ? '' : String(value);
    }
    return this;
  }

  /** Reads an attribute of the first element, or writes one or more. */
  attr(name: string): string | undefined;
  attr(name: string, value: QueryValue): this;
  attr(values: Record<string, QueryValue>): this;
  attr(...rest: unknown[]): string | undefined | this {
    const first = rest[0];
    if (first !== null && typeof first === 'object') {
      for (const el of this.elements) {
        for (const [name, value] of Object.entries(first as Record<string, QueryValue>)) {
          if (value === null || value === false) el.removeAttribute(name);
          else el.setAttribute(name, value === true ? '' : String(value));
        }
      }
      return this;
    }

    const name = String(first);
    if (rest.length < 2) return this.elements[0]?.getAttribute(name) ?? undefined;

    const value = rest[1] as QueryValue;
    for (const el of this.elements) {
      if (value === null || value === false) el.removeAttribute(name);
      else el.setAttribute(name, value === true ? '' : String(value));
    }
    return this;
  }

  /** Removes one or more space-separated attributes. */
  removeAttr(name: string): this {
    const list = names(name);
    for (const el of this.elements) for (const attribute of list) el.removeAttribute(attribute);
    return this;
  }

  /** Reads a property of the first element or writes to all. */
  prop<T = unknown>(name: string): T | undefined;
  prop(name: string, value: unknown): this;
  prop(...rest: unknown[]): unknown {
    const name = String(rest[0]);
    if (rest.length < 2) {
      const el = this.elements[0];
      return el ? (el as unknown as Record<string, unknown>)[name] : undefined;
    }
    for (const el of this.elements) (el as unknown as Record<string, unknown>)[name] = rest[1];
    return this;
  }

  /**
   * Reads and writes `dataset`. Reading converts JSON, numbers, and booleans,
   * so `data-config='{"a":1}'` comes back as an actual object.
   */
  data(): Record<string, unknown>;
  data(key: string): unknown;
  data(key: string, value: unknown): this;
  data(values: Record<string, unknown>): this;
  data(...rest: unknown[]): unknown {
    const first = rest[0];

    if (!rest.length) {
      const el = this.elements[0];
      if (!el) return {};
      const out: Record<string, unknown> = {};
      for (const [key, raw] of Object.entries(el.dataset)) out[key] = parseDataValue(raw);
      return out;
    }

    if (first !== null && typeof first === 'object') {
      for (const el of this.elements) {
        for (const [key, value] of Object.entries(first as Record<string, unknown>)) {
          el.dataset[datasetKey(key)] =
            typeof value === 'string' ? value : JSON.stringify(value ?? null);
        }
      }
      return this;
    }

    const key = datasetKey(String(first));
    if (rest.length < 2) {
      const el = this.elements[0];
      return el ? parseDataValue(el.dataset[key]) : undefined;
    }

    const value = rest[1];
    for (const el of this.elements) {
      el.dataset[key] = typeof value === 'string' ? value : JSON.stringify(value ?? null);
    }
    return this;
  }

  /** Reads a computed style or applies one or more styles. */
  css(property: string): string;
  css(property: string, value: string | number | null): this;
  css(values: Record<string, string | number | null>): this;
  css(...rest: unknown[]): string | this {
    const first = rest[0];
    if (first !== null && typeof first === 'object') {
      for (const el of this.elements) applyStyles(el, first as Record<string, string | number | null>);
      return this;
    }

    const property = String(first);
    if (rest.length < 2) {
      const el = this.elements[0];
      if (!el) return '';
      const name = kebab(property);
      const computed = el.isConnected ? getComputedStyle(el).getPropertyValue(name) : '';
      return (computed || el.style.getPropertyValue(name)).trim();
    }

    for (const el of this.elements) setStyle(el, property, rest[1] as string | number | null);
    return this;
  }

  /** Width in pixels of the first element, or writes to all. */
  width(): number;
  width(value: string | number): this;
  width(...rest: unknown[]): number | this {
    if (!rest.length) {
      const el = this.elements[0];
      return el ? el.getBoundingClientRect().width : 0;
    }
    for (const el of this.elements) setStyle(el, 'width', rest[0] as string | number);
    return this;
  }

  /** Height in pixels of the first element, or writes to all. */
  height(): number;
  height(value: string | number): this;
  height(...rest: unknown[]): number | this {
    if (!rest.length) {
      const el = this.elements[0];
      return el ? el.getBoundingClientRect().height : 0;
    }
    for (const el of this.elements) setStyle(el, 'height', rest[0] as string | number);
    return this;
  }

  /** Position of the first element relative to the document. */
  offset(): QueryPoint {
    const el = this.elements[0];
    if (!el) return { top: 0, left: 0 };
    const rect = el.getBoundingClientRect();
    return { top: rect.top + window.scrollY, left: rect.left + window.scrollX };
  }

  /** Position of the first element relative to the positioned ancestor. */
  position(): QueryPoint {
    const el = this.elements[0];
    if (!el) return { top: 0, left: 0 };
    return { top: el.offsetTop, left: el.offsetLeft };
  }

  /** Reads the vertical scroll of the first element or writes to all. */
  scrollTop(): number;
  scrollTop(value: number): this;
  scrollTop(...rest: unknown[]): number | this {
    if (!rest.length) return this.elements[0]?.scrollTop ?? 0;
    const value = Number(rest[0]) || 0;
    for (const el of this.elements) el.scrollTop = value;
    return this;
  }

  // -------------------------------------------------------------------------
  // Classes
  // -------------------------------------------------------------------------

  /** Adds one or more space-separated classes. */
  addClass(value: string): this {
    const list = names(value);
    if (list.length) for (const el of this.elements) el.classList.add(...list);
    return this;
  }

  /** Removes one or more space-separated classes. */
  removeClass(value: string): this {
    const list = names(value);
    if (list.length) for (const el of this.elements) el.classList.remove(...list);
    return this;
  }

  /** Toggles classes. The second argument forces on or off. */
  toggleClass(value: string, force?: boolean): this {
    const list = names(value);
    for (const el of this.elements) {
      for (const cls of list) {
        if (force === undefined) el.classList.toggle(cls);
        else el.classList.toggle(cls, force);
      }
    }
    return this;
  }

  /** True when some element has all the specified classes. */
  hasClass(value: string): boolean {
    const list = names(value);
    if (!list.length) return false;
    return this.elements.some((el) => list.every((cls) => el.classList.contains(cls)));
  }

  // -------------------------------------------------------------------------
  // DOM manipulation
  // -------------------------------------------------------------------------

  /**
   * Base of `append`, `prepend`, `before`, and `after`. When the collection has more
   * than one element, each destination receives a copy and the last gets the
   * original, which is the expected behavior for those coming from jQuery.
   */
  private insert(content: QueryInput, place: (el: HTMLElement, node: Node) => void): this {
    const total = this.elements.length;
    for (let i = 0; i < total; i++) {
      const el = this.elements[i];
      for (const node of contentNodes(content)) {
        place(el, i === total - 1 ? node : node.cloneNode(true));
      }
    }
    return this;
  }

  /** Inserts content at the end of each element. */
  append(content: QueryInput): this {
    return this.insert(content, (el, node) => el.appendChild(node));
  }

  /** Inserts content at the beginning of each element. */
  prepend(content: QueryInput): this {
    return this.insert(content, (el, node) => el.insertBefore(node, el.firstChild));
  }

  /** Inserts content before each element. */
  before(content: QueryInput): this {
    return this.insert(content, (el, node) => el.parentNode?.insertBefore(node, el));
  }

  /** Inserts content after each element. */
  after(content: QueryInput): this {
    return this.insert(content, (el, node) => el.parentNode?.insertBefore(node, el.nextSibling));
  }

  /** Moves the collection's elements into the target. */
  appendTo(target: QueryInput): this {
    const targets = resolve(target);
    for (let i = 0; i < targets.length; i++) {
      for (const el of this.elements) {
        targets[i].appendChild(i === targets.length - 1 ? el : (el.cloneNode(true) as HTMLElement));
      }
    }
    return this;
  }

  /** Moves the collection's elements to the beginning of the target. */
  prependTo(target: QueryInput): this {
    const targets = resolve(target);
    for (let i = 0; i < targets.length; i++) {
      const parent = targets[i];
      const nodes = this.elements.map((el) =>
        i === targets.length - 1 ? el : (el.cloneNode(true) as HTMLElement)
      );
      for (let j = nodes.length - 1; j >= 0; j--) parent.insertBefore(nodes[j], parent.firstChild);
    }
    return this;
  }

  /** Replaces each element with the provided content, unmounting the old one. */
  replaceWith(content: QueryInput): this {
    for (const el of this.elements) {
      const parent = el.parentNode;
      if (!parent) continue;
      for (const node of contentNodes(content)) parent.insertBefore(node, el);
      destroyNode(el);
      el.remove();
    }
    return this;
  }

  /** Wraps each element with the provided HTML or element. */
  wrap(wrapper: QueryInput): this {
    for (const el of this.elements) {
      const model = resolve(wrapper)[0];
      if (!model) continue;
      const clone = model.cloneNode(true) as HTMLElement;
      el.parentNode?.insertBefore(clone, el);
      let deepest: HTMLElement = clone;
      while (deepest.firstElementChild) deepest = deepest.firstElementChild as HTMLElement;
      deepest.appendChild(el);
    }
    return this;
  }

  /** Removes the parent of each element, keeping children in place. */
  unwrap(): this {
    const parents = new Set<HTMLElement>();
    for (const el of this.elements) {
      const parent = el.parentElement;
      if (parent && parent !== document.body) parents.add(parent);
    }
    for (const parent of parents) {
      const grand = parent.parentNode;
      if (!grand) continue;
      while (parent.firstChild) grand.insertBefore(parent.firstChild, parent);
      destroyNode(parent);
      parent.remove();
    }
    return this;
  }

  /** Removes elements from the document and unmounts reactive effects. */
  remove(): this {
    for (const el of this.elements) {
      destroyNode(el);
      el.remove();
    }
    return this;
  }

  /** Empties elements, unmounting removed content. */
  empty(): this {
    for (const el of this.elements) {
      for (const child of Array.from(el.childNodes)) destroyNode(child);
      el.replaceChildren();
    }
    return this;
  }

  /** Clones elements. The clone starts without directives initialized. */
  clone(deep = true): VoodooCollection {
    return new VoodooCollection(this.elements.map((el) => el.cloneNode(deep) as HTMLElement));
  }

  // -------------------------------------------------------------------------
  // Events
  // -------------------------------------------------------------------------

  /**
   * Listens for events. With the second argument as a string, uses delegation:
   * `on('click', '.item', fn)` continues to work for items created later.
   */
  on(types: string, handler: QueryEventHandler, options?: AddEventListenerOptions): this;
  on(
    types: string,
    selector: string,
    handler: QueryEventHandler,
    options?: AddEventListenerOptions
  ): this;
  on(types: string, ...rest: unknown[]): this {
    const delegated = typeof rest[0] === 'string';
    const selector = delegated ? (rest[0] as string) : null;
    const handler = (delegated ? rest[1] : rest[0]) as QueryEventHandler;
    const options = ((delegated ? rest[2] : rest[1]) as AddEventListenerOptions) ?? {};
    if (typeof handler !== 'function') return this;

    for (const el of this.elements) {
      for (const type of names(types)) {
        const wrapped: EventListener = (event: Event) => {
          if (!selector) {
            handler.call(el, event);
            return;
          }
          const start = event.target as Element | null;
          const matched = start?.closest(selector) as HTMLElement | null;
          if (!matched || !el.contains(matched)) return;
          handler.call(matched, event);
        };
        el.addEventListener(type, wrapped, options);
        bindingsOf(el).push({ type, selector, handler, wrapped, options });
      }
    }
    return this;
  }

  /**
   * Removes listeners registered by `on`. Without arguments removes all, with type
   * removes those for that event, and with selector or function refines further.
   */
  off(types?: string, selectorOrHandler?: string | QueryEventHandler, handler?: QueryEventHandler): this {
    const wantedSelector = typeof selectorOrHandler === 'string' ? selectorOrHandler : null;
    const wantedHandler =
      typeof selectorOrHandler === 'function' ? selectorOrHandler : handler ?? null;
    const wantedTypes = types ? names(types) : null;

    for (const el of this.elements) {
      const list = eventStore.get(el);
      if (!list) continue;
      const keep: BoundEvent[] = [];
      for (const binding of list) {
        const matchType = !wantedTypes || wantedTypes.includes(binding.type);
        const matchSelector = wantedSelector === null || binding.selector === wantedSelector;
        const matchHandler = wantedHandler === null || binding.handler === wantedHandler;
        if (matchType && matchSelector && matchHandler) {
          el.removeEventListener(binding.type, binding.wrapped, binding.options);
        } else {
          keep.push(binding);
        }
      }
      eventStore.set(el, keep);
    }
    return this;
  }

  /** Listens only once. Accepts delegation like `on`. */
  once(types: string, handler: QueryEventHandler): this;
  once(types: string, selector: string, handler: QueryEventHandler): this;
  once(types: string, ...rest: unknown[]): this {
    const delegated = typeof rest[0] === 'string';
    const selector = delegated ? (rest[0] as string) : null;
    const handler = (delegated ? rest[1] : rest[0]) as QueryEventHandler;
    if (typeof handler !== 'function') return this;

    const self = this;
    const wrapper: QueryEventHandler = function (this: HTMLElement, event: Event) {
      if (selector) self.off(types, selector, wrapper);
      else self.off(types, wrapper);
      return handler.call(this, event);
    };

    if (selector) return this.on(types, selector, wrapper);
    return this.on(types, wrapper);
  }

  /**
   * Dispatches an event. Native events with their own method, like `click` and
   * `focus`, use the element's method when there is no `detail`.
   */
  trigger(type: string, detail?: unknown): this {
    for (const el of this.elements) {
      if (detail === undefined && typeof (el as unknown as Record<string, unknown>)[type] === 'function') {
        (el as unknown as Record<string, () => void>)[type]();
        continue;
      }
      const event = new CustomEvent(type, { detail, bubbles: true, cancelable: true });
      (event as unknown as Record<string, unknown>).__voodoo = true;
      el.dispatchEvent(event);
    }
    return this;
  }

  /** Dispatches a custom event that bubbles up the tree, component-style. */
  emit(type: string, detail?: unknown): this {
    for (const el of this.elements) {
      const event = new CustomEvent(type, { detail, bubbles: true, cancelable: true });
      (event as unknown as Record<string, unknown>).__voodoo = true;
      el.dispatchEvent(event);
    }
    return this;
  }

  // -------------------------------------------------------------------------
  // Visibility and animation
  // -------------------------------------------------------------------------

  /** Shows elements by restoring their previous display value. */
  show(): this {
    for (const el of this.elements) showElement(el);
    return this;
  }

  /** Hides elements while saving their current display value. */
  hide(): this {
    for (const el of this.elements) hideElement(el);
    return this;
  }

  /** Toggles visibility. The argument forces show or hide. */
  toggle(force?: boolean): this {
    for (const el of this.elements) {
      const visible = force === undefined ? elementHidden(el) : force;
      if (visible) showElement(el);
      else hideElement(el);
    }
    return this;
  }

  /** Appearance with fade. */
  fadeIn(duration = 220): this {
    for (const el of this.elements) {
      el.removeAttribute('hidden');
      void fadeInElement(el, duration);
    }
    return this;
  }

  /** Disappearance with fade, ending hidden. */
  fadeOut(duration = 220): this {
    for (const el of this.elements) void fadeOutElement(el, duration);
    return this;
  }

  /** Collapses height to zero. */
  slideUp(duration = 240): this {
    for (const el of this.elements) void slideUpElement(el, duration);
    return this;
  }

  /** Expands height to content. */
  slideDown(duration = 240): this {
    for (const el of this.elements) {
      el.removeAttribute('hidden');
      void slideDownElement(el, duration);
    }
    return this;
  }

  /** Toggles between collapse and expand. */
  slideToggle(duration = 240): this {
    for (const el of this.elements) {
      if (elementHidden(el)) {
        el.removeAttribute('hidden');
        void slideDownElement(el, duration);
      } else {
        void slideUpElement(el, duration);
      }
    }
    return this;
  }

  /** Animation via Web Animations API. */
  animate(
    keyframes: Keyframe[] | PropertyIndexedKeyframes,
    options: number | KeyframeAnimationOptions = 300
  ): this {
    for (const el of this.elements) {
      if (typeof el.animate !== 'function') continue;
      el.animate(keyframes, options);
    }
    return this;
  }

  /** Scrolls the page to the first element. */
  scrollIntoView(options: boolean | ScrollIntoViewOptions = { behavior: 'smooth', block: 'start' }): this {
    this.elements[0]?.scrollIntoView(options as ScrollIntoViewOptions);
    return this;
  }

  // -------------------------------------------------------------------------
  // Form
  // -------------------------------------------------------------------------

  /** Serializes the first element's fields as a query string. */
  serialize(): string {
    const el = this.elements[0];
    if (!el) return '';
    const params = new URLSearchParams();
    for (const control of formControls(el)) {
      if (!isSerializable(control)) continue;
      const field = control as HTMLInputElement;
      const select = control as unknown as HTMLSelectElement;
      if (field.tagName === 'SELECT' && select.multiple) {
        for (const option of Array.from(select.selectedOptions)) params.append(field.name, option.value);
        continue;
      }
      params.append(field.name, field.value);
    }
    return params.toString();
  }

  /**
   * Serializes fields into an object. Repeated names and names ending in
   * `[]` become arrays, checkboxes become booleans, and numeric fields become numbers.
   */
  serializeObject(): Record<string, unknown> {
    const el = this.elements[0];
    const out: Record<string, unknown> = {};
    if (!el) return out;

    for (const control of formControls(el)) {
      const field = control as HTMLInputElement;
      if (!field.name || field.disabled) continue;
      const type = (field.getAttribute('type') || '').toLowerCase();
      if (type === 'submit' || type === 'reset' || type === 'button') continue;

      const isList = field.name.endsWith('[]');
      const key = isList ? field.name.slice(0, -2) : field.name;
      const select = control as unknown as HTMLSelectElement;

      let value: unknown;
      if (type === 'checkbox') {
        if (!field.checked && !isList) {
          out[key] = out[key] ?? false;
          continue;
        }
        if (!field.checked) continue;
        value = field.value === 'on' ? true : field.value;
      } else if (type === 'radio') {
        if (!field.checked) continue;
        value = field.value;
      } else if (type === 'file') {
        value = field.multiple ? Array.from(field.files ?? []) : field.files?.[0] ?? null;
      } else if (field.tagName === 'SELECT' && select.multiple) {
        value = Array.from(select.selectedOptions).map((option) => option.value);
      } else if (type === 'number' || type === 'range') {
        value = field.value === '' ? null : Number(field.value);
      } else {
        value = field.value;
      }

      if (isList) {
        const current = out[key];
        if (Array.isArray(current)) current.push(value);
        else out[key] = [value];
        continue;
      }
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        const current = out[key];
        if (Array.isArray(current)) current.push(value);
        else if (current === undefined || current === false) out[key] = value;
        else out[key] = [current, value];
        continue;
      }
      out[key] = value;
    }
    return out;
  }

  /** Sets focus on the first element. */
  focus(options?: FocusOptions): this {
    this.elements[0]?.focus(options);
    return this;
  }

  /** Removes focus from all elements. */
  blur(): this {
    for (const el of this.elements) el.blur();
    return this;
  }

  /** Selects the text of the collection's fields. */
  select(): this {
    for (const el of this.elements) {
      const field = el as HTMLInputElement;
      if (typeof field.select === 'function') field.select();
    }
    return this;
  }

  // -------------------------------------------------------------------------
  // Integration with Voodoo runtime
  // -------------------------------------------------------------------------

  /**
   * Initializes directives for the collection's elements, inheriting the parent's scope.
   * With `force`, unmounts first to restart from scratch.
   */
  walk(force = false): this {
    for (const el of this.elements) {
      if (force) destroyNode(el);
      walkNode(el, findScope(el.parentNode));
    }
    return this;
  }

  /** Unmounts effects, listeners, and components while keeping elements in the DOM. */
  destroy(): this {
    for (const el of this.elements) destroyNode(el);
    return this;
  }
}

// ---------------------------------------------------------------------------
// Module API
// ---------------------------------------------------------------------------

/**
 * Creates a collection from a CSS selector, element, list of elements,
 * HTML string, or function.
 *
 * ```js
 * V.query('#list li')           // selector
 * V.query(document.body)        // element
 * V.query('<li>new</li>')       // creates elements
 * V.query(() => start())        // equivalent to V.ready
 * ```
 *
 * @param input selector, node, list, HTML or initialization function
 * @param context optional search root, useful for local scopes
 */
export function query(input?: QueryInput, context?: QueryInput): VoodooCollection {
  if (typeof input === 'function') {
    ready(input as ReadyCallback);
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    return new VoodooCollection(root ? [root] : []);
  }
  return new VoodooCollection(resolve(input, context));
}

/**
 * Executes the function when Voodoo considers the document ready, and returns a
 * promise for the same moment. Both forms work:
 *
 * ```js
 * V.ready(() => console.log('pronto'))
 * await V.ready()
 * ```
 *
 * The library's own scheduler decides the time, waiting for the body to exist
 * and the tree to stop growing. This does not listen to `DOMContentLoaded`.
 */
export function ready(fn?: ReadyCallback): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();

  return new Promise<void>((resolve) => {
    whenBodyReady(() => {
      try {
        fn?.();
      } catch (err) {
        handleError(err, 'V.ready');
      }
      resolve();
    });
  });
}

/** Creates elements from an HTML string without inserting them in the document. */
export function fromHtml(html: string): VoodooCollection {
  return new VoodooCollection(parseHtml(html));
}
